package ai.jessie.web.invoices

import ai.jessie.shared.invoices.InvoiceProgressResponse
import ai.jessie.shared.invoices.InvoiceTransitionRequest
import ai.jessie.web.onboarding.apiRequest
import ai.jessie.web.onboarding.validOrganisationId
import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.serialization.json.Json

// UC-014. The invoice's current state, permitted next states and history (step 4), and one
// confirmed transition carrying the state the member saw (steps 8-10, B5). The server decides
// every transition; the browser only proposes one.
interface InvoiceProgressPort {
    suspend fun progress(organisationId: String, invoiceId: String): InvoiceProgressResponse

    suspend fun transition(
        organisationId: String,
        invoiceId: String,
        request: InvoiceTransitionRequest,
    ): InvoiceProgressResponse
}

val LocalInvoiceProgressPort =
    staticCompositionLocalOf<InvoiceProgressPort> {
        error("Invoice progress port must be supplied by the application")
    }

class BrowserInvoiceProgressPort : InvoiceProgressPort {
    private val json = Json { ignoreUnknownKeys = true }

    override suspend fun progress(organisationId: String, invoiceId: String) =
        answer(invoiceId, request(organisationId, "${invoicePath(invoiceId)}/progress"))

    override suspend fun transition(
        organisationId: String,
        invoiceId: String,
        request: InvoiceTransitionRequest,
    ) =
        answer(
            invoiceId,
            request(
                organisationId,
                "${invoicePath(invoiceId)}/transitions",
                json.encodeToString(request),
            ),
        )

    // Only the requested invoice's progress is accepted.
    private fun answer(invoiceId: String, text: String) =
        json.decodeFromString<InvoiceProgressResponse>(text).also {
            require(it.invoice.id.equals(invoiceId, ignoreCase = true))
        }

    private suspend fun request(
        organisationId: String,
        path: String,
        body: String? = null,
    ): String {
        require(validOrganisationId(organisationId))
        return apiRequest(json, "/organisations/$organisationId/invoices$path", body) { status, text
            ->
            json.invoiceFailure(status, text)
        }
    }
}

private fun invoicePath(invoiceId: String): String {
    require(validInvoiceId(invoiceId))
    return "/$invoiceId"
}
